<template>
    <v-row>
        <v-col sm="8">
            <v-card-title>
                {{ data.details.originTown }} <v-icon x-small class="_arrow">fas fa-arrow-right</v-icon>
                {{ data.details.unloadingTown }}
            </v-card-title>
            <v-card elevation="1">
                <v-card-text>
                    <div class="milestones" ref="milestones">
                        <div class="milestone" v-for="(milestone, index) in data.details.milestones" :key="index">
                            <div class="center label-major-steps activity-name">
                                {{
                                    $te(`trackandtrace.majorSteps.${milestone.activityName.replace(' ', '_')}`)
                                        ? $t(`trackandtrace.majorSteps.${milestone.activityName.replace(' ', '_')}`)
                                        : milestone.activityName.replace(' ', '_')
                                }}
                            </div>
                            <i :class="['default-icon', getIconClass(milestone.activityName)]"></i>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <div
                                        v-bind="attrs"
                                        v-on="on"
                                        :style="`color: ${getColor(milestone, index)};`"
                                        :class="`progress ${milestone.milestoneConfirmation === '1' ? 'active' : ''}`"
                                    >
                                        <small>{{ milestone.partyName }}</small>
                                        <small>{{ milestone.actualArrivalDate }}</small>
                                    </div>
                                </template>
                                <div>
                                    <div class="center">{{ milestone.partyName }}</div>
                                    <div class="center" v-if="milestone.milestoneConfirmation === '1'">
                                        {{ milestone.actualArrivalDate }} - {{ milestone.actualArrivalTime }}
                                    </div>
                                    <div class="center" v-else>
                                        {{ milestone.estimatedArrivalDate }} - {{ milestone.estimatedArrivalTime }}
                                    </div>
                                </div>
                            </v-tooltip>
                        </div>
                    </div>

                    <v-row class="informations">
                        <v-col col="4">
                            <v-card elevation="0" class="d-flex flex-column">
                                <v-card-title>{{ $t('trackandtrace.details.origin') }}:</v-card-title>

                                <v-card-text style="margin-bottom: 20px">
                                    <b>{{ data.details.originName || data.details.loadingCorporateName }}</b>
                                    <p>{{ data.details.originAddress }}</p>
                                    <b>{{ data.details.originZipCode }} {{ data.details.originTown }}</b>
                                    <b>{{ data.details.originCountry }}</b>
                                </v-card-text>
                                <v-spacer></v-spacer>
                                <v-card-actions class="mt-2">
                                    <small class="small">{{ $t('trackandtrace.details.departure') }}</small>
                                    <small class="small"
                                        >{{ data.details.estimatedStartingDate }} {{ data.details.minimumStartingDate }}
                                        {{ $t('trackandtrace.details.estimatedM') }}</small
                                    >
                                    <small class="small mea"
                                        >{{ data.details.actualStartingDate }} {{ data.details.actualStartingTime }}
                                        {{ $t('trackandtrace.details.actualM') }}</small
                                    >
                                </v-card-actions>
                            </v-card>
                        </v-col>

                        <v-col cols="4">
                            <v-card elevation="0" class="d-flex flex-column">
                                <v-card-title>{{ $t('trackandtrace.details.destination') }}:</v-card-title>
                                <v-card-text>
                                    <b>{{ data.details.unloadingCorporateName }}</b>
                                    <p>{{ data.details.unloadingAddress }}</p>
                                    <b>{{ data.details.unloadingArea }} {{ data.details.unloadingTown }}</b>
                                    <b>{{ data.details.unloadingCountry }}</b>
                                </v-card-text>
                                <v-spacer></v-spacer>
                                <v-card-actions class="mt-2">
                                    <small class="small">{{ $t('trackandtrace.details.delivery') }}</small>
                                    <small class="small"
                                        >{{ data.details.estimatedEndingDate }} {{ data.details.minimumEndingTime }}
                                        {{ $t('trackandtrace.details.estimatedF') }}</small
                                    >
                                    <small class="small mea"
                                        >{{ data.details.actualEndingDate }} {{ data.details.actualEndingTime }}
                                        {{ $t('trackandtrace.details.actualF') }}</small
                                    >
                                </v-card-actions>
                            </v-card>
                        </v-col>
                        <v-col cols="4" class="references">
                            <v-card elevation="0">
                                <v-card-title>{{ $t('trackandtrace.details.references') }}:</v-card-title>
                                <v-card-text>
                                    <div v-for="(referenceConfig, index) in this.getDetailsReferences().detailsConfig" :key="index">
                                        <div v-if="referenceConfig && refIsVisible(referenceConfig.value)">
                                            <small>{{ getTranslation(referenceConfig.value) }}</small>
                                            <div class="horizontal-scroll" v-if="referenceConfig.wsName !== 'orderNumber'">
                                                {{ data.details[referenceConfig.wsName] }}
                                            </div>
                                            <div v-else class="horizontal-scroll">
                                                {{ data.details[referenceConfig.wsName] }}
                                                <br v-if="data.details[referenceConfig.wsName]" />
                                                {{ data.details['orders'] }}
                                            </div>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-col>
        <v-col sm="4">
            <v-card elevation="0" class="details-map">
                <v-card-text>
                    <Map
                        v-if="data"
                        :departure="`${data.details.originAddress} ${data.details.originTown} ${data.details.originCountry}`"
                        :arrival="`${data.details.unloadingAddress} ${data.details.unloadingTown} ${data.details.unloadingCountry}`"
                        :coordinates="data.details.coordinates"
                    />
                    <div v-if="lastEvent">
                        <h3>{{ $t('trackandtrace.details.lastEvent') }}:</h3>
                        <b>{{ lastEvent.event.referenceValue }}</b>
                        <br />
                        {{ lastEvent.whenDate }} {{ lastEvent.whenTime }}
                        <br />
                        {{ lastEvent.whenTimeZone }}
                        <br />
                        {{ lastEvent.whereTown.referenceValue }}
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import { SHIPMENT, GET_DETAILS_REFERENCES } from '@/constants/shipment';
import Map from '@/components/Map';

export default {
    components: { Map },
    props: ['data'],
    data() {
        return {
            firstRender: true
        };
    },
    computed: {
        ...mapGetters(['getClientName']),
        lastEvent() {
            return this.data.events[this.data.events.length - 1];
        },
        clientId() {
            const id = this.getDetailsReferences()?.id;
            if (id === 'T&T_default') return 'default';
            return id;
        }
    },
    methods: {
        ...mapGetters(SHIPMENT, [GET_DETAILS_REFERENCES]),
        getTranslation(name) {
            const key = `trackAndTraceSearch.${this.$store.getters.getCurrentClientId}.detailsTab.${name}`;
            return this.$te(key) ? this.$t(key) : this.$t(`trackAndTraceSearch.default.detailsTab.${name}`);
        },
        getDatetime(date, time) {
            if (date && date.length > 0) {
                const d1 = date.split('/');

                return new Date(`${d1[1]}/${d1[0]}/${d1[2]} ${time}`).getTime();
            }
        },
        compareDate(firstDate, firstTime, secondDate, secondTime) {
            const firstDatetime = this.getDatetime(firstDate, firstTime);
            const secondDatetime = this.getDatetime(secondDate, secondTime);

            if (firstDatetime === secondDatetime) {
                return 0;
            }
            return firstDatetime > secondDatetime ? -1 : 1;
        },
        getColor(milestone, index) {
            const res = this.compareDate(
                milestone.actualArrivalDate,
                milestone.actualArrivalTime,
                milestone.estimatedArrivalDate,
                milestone.estimatedArrivalTime
            );
            if (milestone.milestoneConfirmation !== '1') {
                return null;
            } else if (this.data.details.milestones[index + 1]?.milestoneConfirmation !== '1') {
                this.$nextTick(() => {
                    if (this.firstRender && this.$refs.milestones) {
                        this.$refs.milestones.scrollLeft += index * 116;
                    }
                    this.firstRender = false;
                });
            }
            return res === -1 ? 'red' : 'green';
        },
        refIsVisible(value) {
            return (
                this.getDetailsReferences()?.detailsConfig.filter(refConfig => refConfig?.value === value && refConfig.visible)
                    .length > 0
            );
        },
        getIconClass(activityName) {
            return activityName.replace(' ', '_');
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@/scss/_colors.scss';

@font-face {
    font-family: 'icomoon';
    src: url('../../../assets/fonts/icomoon.eot'); /* IE9 Compat Modes */
    src: url('../../../assets/fonts/icomoon.eot?#iefix') format('embedded-opentype'),
        /* IE6-IE8 */ url('../../../assets/fonts/icomoon.woff') format('woff'),
        /* Pretty Modern Browsers */ url('../../../assets/fonts/icomoon.ttf') format('truetype'),
        /* Safari, Android, iOS */ url('../../../assets/fonts/icomoon.svg#svgFontName') format('svg'); /* Legacy iOS */
}

._arrow {
    margin: 0 10px;
}
i {
    font-style: normal;
}
.milestones {
    i:before {
        font-size: 8rem;
        line-height: 4rem;
        font-family: icomoon;
        margin: 0;
        color: $primary;
    }
    i.default-icon:before {
        content: '\e900';
    }
    i.CO-CH:before {
        content: '\e901';
    }
    i.CO-DE:before {
        content: '\e901';
    }
    i.CHARGEMENT:before {
        content: '\e92a';
    }
    i.DEBARQUEME:before {
        content: '\e921';
    }
    i.DECHARGEME:before {
        content: '\e933';
    }
    i.EMBARQUEME:before {
        content: '\e921';
    }
    i.CROSS_DOCK:before {
        content: '\e922';
    }
    i.CRDOCKEXP:before {
        content: '\e922';
    }
    i.CRDOCKIMP:before {
        content: '\e922';
    }
    i.DOUANEEXP:before {
        content: '\e928';
    }
    i.DOUANEIMP:before {
        content: '\e928';
    }
    i.BOOKING:before {
        content: '\e945';
    }
    i.PASSQUAI:before {
        content: '\e922';
    }
    i.MISEAQUAI:before {
        content: '\e922';
    }
    i.DELIVERY:before {
        content: '\e933';
    }
    i.SHUTTLE:before {
        content: '\e923';
    }
    i.PICKUP:before {
        content: '\e92a';
    }
    i.SCO_TRANSPORT_AD:before {
        content: '\e920';
    }
    i.ROAD:before {
        content: '\e923';
    }
    i.COLLECT:before {
        content: '\e92a';
    }
    i.CUSTOMS:before {
        content: '\e928';
    }
    i.COURSE:before {
        content: '\e923';
    }
    i.TRANSPORT:before {
        content: '\e920';
    }
    i.TOWER:before {
        content: '\e932';
    }
    i.IN_TRANSIT:before {
        content: '\e922';
    }
    i.FINAL_DESTINATION:before {
        content: '\e928';
    }
    i.WAREHOUSE:before {
        content: '\e922';
    }
    i.AIR:before {
        content: '\e921';
    }
    i.AIR_CONSOL:before {
        content: '\e921';
    }
    i.AIR_CONSOL2:before {
        content: '\e921';
    }
    i.EXPRESS:before {
        content: '\e921';
    }
    i.AIR_CHARTER:before {
        content: '\e921';
    }
    i.ROAD_CONSOL:before {
        content: '\e920';
    }
    i.ROAD_CONSOL2:before {
        content: '\e920';
    }
    i.ROAD_DIRECT:before {
        content: '\e920';
    }
    i.SEA:before {
        content: '\e930';
    }
    i.SEA_CHARTER:before {
        content: '\e930';
    }
    i.RAIL:before {
        content: '\e931';
    }
}
.horizontal-scroll {
    overflow-x: auto;
    white-space: nowrap;
}
.center {
    text-align: center;
}
.activity-name {
    min-height: 46px;
}
.milestones {
    width: min-content;
    max-width: 100%;
    display: flex;
    overflow-x: auto;
    margin: 0 auto;
    text-align: center;
    counter-reset: step;
    box-sizing: border-box;
    padding: 0 24px;
    small {
        display: block;
        font-size: 0.6rem;
        white-space: break-spaces;
    }
}
.milestone {
    flex: 0 0 116px;
    margin: 24px 0;
}
.progress {
    width: 100%;
    position: relative;
    z-index: 2;
    padding: 0 6px;
    &:before {
        content: counter(step);
        counter-increment: step;
        width: 30px;
        height: 30px;
        border: 2px solid #bebebe;
        display: block;
        margin: 0 auto 10px auto;
        border-radius: 50%;
        line-height: 27px;
        background: white;
        color: #bebebe;
        text-align: center;
        font-weight: bold;
    }
}
.progress.active:before {
    border-color: $primary;
    background: $primary;
    color: white;
}
.informations {
    .v-card__text,
    .v-card__actions {
        color: #444;
        small {
            display: block;
            color: #888;
            font-weight: normal;
            margin-top: 6px;
            line-height: 1rem;
        }
        b {
            display: block;
        }
        p {
            margin: 0;
            color: #888;
        }
        .mea {
            color: $primary;
        }
        .small {
            margin-top: 0;
        }
    }
}
.informations .v-card {
    height: 100%;
}
.informations .v-card__title,
h3 {
    color: $primary;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    font-weight: 400 !important;
}
.informations .v-card__text {
    padding: 0;
}
.informations .v-card__actions {
    flex-direction: column;
    padding: 0;
    align-items: flex-start;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 12px;
    position: relative;
    &:before {
        content: '';
        position: absolute;
        top: -10px;
        left: 0;
        width: 80%;
        height: 1px;
        background-color: #d5d5d5;
    }
}
.references {
    text-align: center;
    .v-card {
        display: inline-block;
        text-align: left;
    }
    .v-card__text {
        font-family: 'IBM Plex Mono', monospace;
        font-weight: 700;
        font-size: 12px;
        p {
            margin-bottom: 8px;
        }
    }
}
.details-map {
    .map {
        height: 300px;
        margin-bottom: 16px;
    }
}
@media screen and (min-width: 800px) {
    .milestones {
        flex: 0 0 150px;
        i:before {
            font-size: 13rem;
            line-height: 6rem;
        }
        small {
            font-size: 0.7rem;
        }
    }
}
@media screen and (max-width: 480px) {
    .informations {
        flex-direction: column;
        .v-card__actions::before {
            display: none;
        }
        > div {
            max-width: 100%;
        }
        .references {
            text-align: left;
        }
    }
}
</style>
